import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';
import { isWhiteLabel } from '../../config/tenantConfig';
import Tooltip from './Tooltip';

interface DropdownOption {
  value: string;
  label: string;
  /** Rendered to the left of the label — e.g. a coloured status dot. */
  leftContent?: React.ReactNode;
  /** Rendered to the right of the label — e.g. a `(count)` badge. */
  rightContent?: React.ReactNode;
  /** Always-on hover-tooltip text. When set, shown on the option row in the
   *  open panel AND on the trigger when this option is the selected one —
   *  for opaque labels like abbreviations ("CP as SP") that need explanation. */
  tooltip?: string;
}

interface DropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  size?: 'sm' | 'md';
  position?: 'bottom' | 'top';
  disabled?: boolean;
  className?: string;
  /**
   * Accessible name for the trigger button. Use when the dropdown has no
   * visible <label> (e.g. compact filter bars) so screen readers — and
   * label-based test queries — can identify the control. Mirrors the
   * `aria-label` a native <select> would carry.
   */
  ariaLabel?: string;
  /**
   * Render the options panel in a portal with fixed positioning, so it escapes
   * `overflow:auto` ancestors (e.g. the horizontally-scrollable leg matrix).
   * Closes on scroll/resize since the fixed panel can't follow the trigger.
   */
  portal?: boolean;
}

const sizeStyles = {
  sm: {
    button: 'text-xs px-2 py-1.5 rounded-md gap-1',
    chevron: 'w-3 h-3',
    panel: 'rounded-md',
    option: 'px-2 py-1.5 text-xs',
  },
  md: {
    button: 'text-sm px-3 py-2 rounded-lg gap-2',
    chevron: 'w-4 h-4',
    panel: 'rounded-lg',
    option: 'px-3 py-2 text-sm',
  },
};

/** One option row. Shows a tooltip with the full label only when truncated;
 *  the tooltip is portaled so it isn't clipped by the panel's overflow. */
const DropdownOptionRow: React.FC<{
  option: DropdownOption;
  className: string;
  onSelect: () => void;
  selected: boolean;
  buttonRef: React.Ref<HTMLButtonElement> | null;
}> = ({ option, className, onSelect, selected, buttonRef }) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);
  useEffect(() => {
    const node = labelRef.current;
    if (!node) return;
    const measure = () => setTruncated(node.scrollWidth > node.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [option.label]);
  return (
    <button
      ref={buttonRef}
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      className={className}
    >
      {option.leftContent}
      <Tooltip
        portal
        position="bottom"
        text={option.tooltip || (truncated ? option.label : '')}
        className="flex-1 min-w-0"
      >
        <span ref={labelRef} className="block w-full truncate">{option.label}</span>
      </Tooltip>
      {option.rightContent}
    </button>
  );
};

const Dropdown: React.FC<DropdownProps> = ({ value, onChange, options, size = 'md', position = 'bottom', disabled, className, portal = false, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const [fixedStyle, setFixedStyle] = useState<React.CSSProperties | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [labelTruncated, setLabelTruncated] = useState(false);
  const s = sizeStyles[size];

  const selectedOpt = options.find(o => o.value === value);
  const selectedLabel = selectedOpt?.label || value;
  const wl = isWhiteLabel();
  const focusRing = wl ? 'focus:ring-zebu-500' : 'focus:ring-blue-500';
  const selectedOption = wl
    ? 'bg-zebu-50 dark:bg-zebu-900/30 text-zebu-700 dark:text-zebu-300 font-medium'
    : 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium';

  // Close when clicking outside the trigger AND outside the (possibly portaled) panel.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current && ref.current.contains(t)) return;
      if (panelRef.current && panelRef.current.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Portal mode: compute fixed coords from the trigger; flip up when low on space.
  useLayoutEffect(() => {
    if (!open || !portal || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const maxH = 240;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < maxH && r.top > spaceBelow;
    setFixedStyle({
      position: 'fixed',
      left: Math.round(r.left),
      width: Math.round(r.width),
      zIndex: 9999,
      ...(openUp
        ? { bottom: Math.round(window.innerHeight - r.top + 4) }
        : { top: Math.round(r.bottom + 4) }),
    });
  }, [open, portal]);

  // Portal mode: the fixed panel can't follow the trigger, so close on outer
  // scroll/resize. Ignore scrolls that originate inside the panel itself — the
  // centering effect below sets scrollTop (and the user can scroll the options
  // list), both of which fire a scroll event we must NOT treat as "close".
  useEffect(() => {
    if (!open || !portal) return;
    const onScroll = (e: Event) => {
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, portal]);

  // Center the selected option in the panel whenever it opens.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const selected = selectedRef.current;
    if (!panel || !selected) return;
    panel.scrollTop = selected.offsetTop - panel.clientHeight / 2 + selected.clientHeight / 2;
  }, [open, fixedStyle]);

  // Surface the full selected label in a tooltip ONLY when it's truncated.
  // ResizeObserver re-measures on container resize (e.g. leg-matrix column
  // width changes); guarded for jsdom where ResizeObserver is absent.
  useEffect(() => {
    const node = labelRef.current;
    if (!node) return;
    const measure = () => setLabelTruncated(node.scrollWidth > node.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [selectedLabel]);

  const optionButtons = options.map(o => (
    <DropdownOptionRow
      key={o.value}
      option={o}
      selected={o.value === value}
      onSelect={() => { onChange(o.value); setOpen(false); }}
      buttonRef={o.value === value ? selectedRef : null}
      className={cn(
        'w-full text-left transition-colors flex items-center gap-1.5',
        s.option,
        o.value === value
          ? selectedOption
          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700',
      )}
    />
  ));

  const panelBase = 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-lg overflow-hidden max-h-60 overflow-y-auto min-w-[100px]';

  return (
    <div ref={ref} className={cn('relative', className)}>
      <Tooltip
        portal
        text={!open ? (selectedOpt?.tooltip || (labelTruncated ? selectedLabel : '')) : ''}
        className="w-full"
      >
        <button
          type="button"
          onClick={() => !disabled && setOpen(prev => !prev)}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            `flex items-center justify-between w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-1 ${focusRing} transition-colors`,
            s.button,
            disabled && 'opacity-40 cursor-not-allowed',
          )}
        >
          <span className="inline-flex items-center gap-1.5 truncate">
            {selectedOpt?.leftContent}
            <span ref={labelRef} className="truncate">{selectedLabel}</span>
            {selectedOpt?.rightContent}
          </span>
          <ChevronDown className={cn(s.chevron, 'text-gray-400 shrink-0 transition-transform duration-200', open && 'rotate-180')} />
        </button>
      </Tooltip>

      {open && portal && fixedStyle &&
        createPortal(
          <div ref={panelRef} role="listbox" aria-label={ariaLabel} style={fixedStyle} className={cn(panelBase, s.panel)}>
            {optionButtons}
          </div>,
          document.body,
        )}

      {open && !portal && (
        <div
          ref={panelRef}
          role="listbox"
          aria-label={ariaLabel}
          className={cn(
            'absolute z-20 w-full',
            position === 'top' ? 'bottom-full mb-1' : 'mt-1',
            panelBase,
            s.panel,
          )}
        >
          {optionButtons}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
